/**
 * Domain constants shared between the API, the web panel and the edge Worker.
 *
 * Domain vocabulary is kept in Portuguese on purpose: the domain is Brazilian
 * legal/administrative and translating terms like "concluinte" or "balancete"
 * would only create ambiguity. See CLAUDE.md § Convenções.
 */

// ---------------------------------------------------------------------------
// Perfis de acesso — ver documentacoes/07-perfis-e-permissoes.md
// ---------------------------------------------------------------------------

export const PAPEIS = [
  'admin', // administrador técnico da plataforma
  'admin_master_egba', // administrador master da EGBA: parametriza preços e regras
  'egba', // equipe operacional da Empresa Gráfica da Bahia
  'parceiro', // revenda / contador / associação
  'cliente', // escola ou empresa que publica
  'operador', // usuário operacional da organização cliente
  'leitura', // auditoria / consulta
] as const

export type Papel = (typeof PAPEIS)[number]

/** Papéis que exigem MFA obrigatoriamente. */
export const PAPEIS_COM_MFA_OBRIGATORIO: readonly Papel[] = [
  'admin',
  'admin_master_egba',
  'egba',
]

/**
 * Papéis que podem alterar parâmetros de negócio: tabela de preços, categorias,
 * janela eleitoral, prazos, textos legais.
 *
 * A EGBA é dona destes números — a plataforma não decide preço. Por isso o
 * `admin_master_egba` existe separado do `egba` operacional: quem opera a fila
 * de publicação não é necessariamente quem pode mudar quanto custa.
 */
export const PAPEIS_QUE_PARAMETRIZAM: readonly Papel[] = ['admin', 'admin_master_egba']

// ---------------------------------------------------------------------------
// Compliance eleitoral — Lei 9.504/97, art. 73, VI, "b"
// Ver documentacoes/02-compliance-eleitoral.md
// ---------------------------------------------------------------------------

/**
 * Classificação obrigatória de toda peça de comunicação.
 * Não existe default: peça sem classificação não é enviada (fail-closed).
 */
export const CLASSES_COMUNICACAO = [
  /** Resposta a uma ação do próprio titular (confirmação, comprovante, senha). */
  'transacional',
  /** Aviso de dever legal a quem já é lead/cliente cadastrado. */
  'obrigacional',
  /** Divulgação de atos, programas, obras, serviços e campanhas do órgão. */
  'institucional',
  /** Prospecção fria, mídia paga, retargeting, captação de novos leads. */
  'promocional',
] as const

export type ClasseComunicacao = (typeof CLASSES_COMUNICACAO)[number]

/** Classes que continuam permitidas dentro da janela eleitoral vedada. */
export const CLASSES_PERMITIDAS_EM_PERIODO_ELEITORAL: readonly ClasseComunicacao[] = [
  'transacional',
  'obrigacional',
]

// ---------------------------------------------------------------------------
// Canais de comunicação
// ---------------------------------------------------------------------------

export const CANAIS = ['email', 'sms', 'whatsapp', 'push', 'meta_ads', 'google_ads'] as const
export type Canal = (typeof CANAIS)[number]

/**
 * Canais que exigem opt-in explícito e registrado antes de qualquer envio.
 * WhatsApp é exigência da Meta e da LGPD — não há flag de bypass.
 * Ver documentacoes/03-lgpd-e-dados.md § 3.
 */
export const CANAIS_COM_OPTIN_OBRIGATORIO: readonly Canal[] = ['whatsapp']

// ---------------------------------------------------------------------------
// Situação regulatória — Resolução CEE 44/2020, Art. 5º
// ---------------------------------------------------------------------------

export const SITUACOES_REGULATORIAS = [
  'credenciada',
  'protocolada_em_tramitacao',
  'irregular',
  'desconhecida',
] as const

export type SituacaoRegulatoria = (typeof SITUACOES_REGULATORIAS)[number]

/**
 * Res. CEE 44/2020, Art. 5º — instituição sem credenciamento atualizado não tem
 * concluintes publicados. O §1º libera quem tem processo protocolado e em
 * tramitação no CEE/BA.
 */
export const SITUACOES_QUE_PERMITEM_PUBLICAR: readonly SituacaoRegulatoria[] = [
  'credenciada',
  'protocolada_em_tramitacao',
]

// ---------------------------------------------------------------------------
// Pedidos de publicação
// ---------------------------------------------------------------------------

// STATUS_PEDIDO e StatusPedido mudaram de casa: agora vivem em
// `intake/estados.ts`, ao lado da tabela de transições. Duas listas de status,
// uma sem `aguardando_aprovacao` e `aguardando_sec`, seriam duas verdades — e a
// que a validação usasse não seria necessariamente a que a máquina de estados
// aceita.

// ---------------------------------------------------------------------------
// Categorias de publicação (seed inicial)
// A categoria é DADO, não código — ver documentacoes/05-modelo-de-dados.md
// ---------------------------------------------------------------------------

export const CATEGORIA_ESCOLAS = 'escolas-concluintes'
export const CATEGORIA_BALANCOS = 'balancos-balancetes'

/** CNAEs que caracterizam o universo da Resolução CEE 44/2020. */
export const CNAES_RESOLUCAO_44 = {
  ENSINO_MEDIO: '8520100',
  EDUCACAO_PROFISSIONAL_TECNICA: '8541400',
} as const

/**
 * Colunas proibidas em qualquer arquivo de concluintes.
 * A Res. 44/2020 exige apenas a relação NOMINAL — nada além disso é necessário,
 * e a maioria dos concluintes do Ensino Médio é menor de idade.
 * O validador REJEITA o arquivo, não apenas avisa.
 * Ver documentacoes/03-lgpd-e-dados.md § 1.2.
 */
export const CAMPOS_PROIBIDOS_CONCLUINTES = [
  'cpf',
  'rg',
  'documento',
  'identidade',
  'data_nascimento',
  'nascimento',
  'dt_nasc',
  'endereco',
  'logradouro',
  'telefone',
  'celular',
  'email',
  'filiacao',
  'mae',
  'pai',
  'responsavel',
  'nota',
  'media',
  'nis',
  'sus',
  'titulo_eleitor',
] as const

// ---------------------------------------------------------------------------
// Motivos de supressão
// ---------------------------------------------------------------------------

export const MOTIVOS_SUPRESSAO = [
  'opt_out',
  'bounce_hard',
  'reclamacao',
  'manual',
  'judicial',
] as const

export type MotivoSupressao = (typeof MOTIVOS_SUPRESSAO)[number]
